import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import SEOHead from '../components/common/SEOHead';
import MatchGate from '../components/match/MatchGate';
import MatchPreview from '../components/match/MatchPreview';
import MatchResults from '../components/match/MatchResults';
import { ArrowRight, ArrowLeft, Check } from 'lucide-react';

const REQUIRED_DEFAULTS = {
    budget_max: '',
    down_payment: '',
    annual_income: '',
    investment_horizon: '',
    lifestyle_priorities: [],
    pets: '',
    dining_importance: 3,
    nightlife_importance: 3,
    life_stage: '',
    schools_importance: 3,
    safety_importance: 3,
    commute_destination: '',
    commute_max_min: '',
    transit_importance: 3,
    appreciation_importance: 3,
};

export default function Match() {
    const { user } = useAuth();
    const nav = useNavigate();
    const { search } = useLocation();

    const [sections, setSections] = useState([]);
    const [sectionIdx, setSectionIdx] = useState(0);
    const [answers, setAnswers] = useState(REQUIRED_DEFAULTS);
    const [email, setEmail] = useState(user?.email || '');
    const [firstName, setFirstName] = useState(user?.name?.split(' ')[0] || '');
    const [phase, setPhase] = useState('intro'); // intro | assessment | submitting | preview | results
    const [preview, setPreview] = useState(null);
    const [results, setResults] = useState(null);
    const [gateOpen, setGateOpen] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        (async () => {
            const { data } = await api.get('/match/schema');
            setSections(data.sections || []);
        })();
    }, []);

    // If arriving from /match/unlock?sid=... (post-OAuth), fetch full results
    useEffect(() => {
        const params = new URLSearchParams(search);
        const sid = params.get('unlock_sid');
        if (sid && user) {
            (async () => {
                try {
                    const { data } = await api.post('/match/unlock', { session_id: sid });
                    setResults(data.results);
                    setPhase('results');
                    nav('/match', { replace: true });
                } catch (_e) { /* ignore */ }
            })();
        }
    }, [search, user, nav]);

    const setAns = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));
    const toggleMulti = (k, val) => setAnswers((a) => ({
        ...a,
        [k]: a[k].includes(val) ? a[k].filter((v) => v !== val) : [...a[k], val],
    }));

    const currentSection = sections[sectionIdx];

    const validateSection = () => {
        if (!currentSection) return false;
        for (const q of currentSection.questions) {
            if (!q.required) continue;
            const v = answers[q.id];
            if (v === '' || v === null || v === undefined) return false;
            if (q.type === 'number' && (isNaN(Number(v)) || Number(v) <= 0)) return false;
        }
        return true;
    };

    const next = () => {
        setError('');
        if (!validateSection()) {
            setError('Please answer every required question in this section.');
            return;
        }
        if (sectionIdx < sections.length - 1) {
            setSectionIdx((i) => i + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            submit();
        }
    };

    const submit = async () => {
        setPhase('submitting');
        setError('');
        try {
            const payload = {
                answers: {
                    ...answers,
                    budget_max: Number(answers.budget_max),
                    down_payment: Number(answers.down_payment || 0),
                    annual_income: Number(answers.annual_income),
                    commute_max_min: Number(answers.commute_max_min),
                    dining_importance: Number(answers.dining_importance),
                    nightlife_importance: Number(answers.nightlife_importance),
                    schools_importance: Number(answers.schools_importance),
                    safety_importance: Number(answers.safety_importance),
                    transit_importance: Number(answers.transit_importance),
                    appreciation_importance: Number(answers.appreciation_importance),
                },
                email: email || undefined,
                first_name: firstName || undefined,
            };
            const { data } = await api.post('/match/submit', payload);
            setPreview(data);
            if (user) {
                // If already logged in, unlock immediately
                const full = await api.post('/match/unlock', { session_id: data.session_id });
                setResults(full.data.results);
                setPhase('results');
            } else {
                setPhase('preview');
            }
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (e) {
            setError('Could not submit — please try again.');
            setPhase('assessment');
        }
    };

    const startAssessment = () => setPhase('assessment');

    return (
        <div className="bg-[#FAFAF8] min-h-screen">
            <SEOHead
                title="Neighbourhood Match Assessment · Southern Ontario"
                description="A 4-section assessment that ranks every Southern Ontario neighbourhood against your budget, lifestyle, family and investment goals. Free."
                path="/match"
            />

            {/* ==================== INTRO ==================== */}
            {phase === 'intro' && (
                <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-20">
                    <div className="max-w-3xl">
                        <div className="text-[11px] tracking-[0.24em] uppercase text-neutral-500 font-mono-data flex items-center gap-3">
                            <span className="inline-block w-6 h-px bg-neutral-400" />
                            The BestNeighbourhoods Assessment
                        </div>
                        <h1
                            className="font-editorial mt-8 leading-[1.02] text-black"
                            style={{ fontSize: 'clamp(48px, 7vw, 96px)', letterSpacing: '-0.03em' }}
                            data-testid="match-intro-title"
                        >
                            Fifteen questions.
                            <br />
                            <span className="italic text-neutral-500">One honest match report.</span>
                        </h1>
                        <p className="mt-8 font-editorial text-neutral-700 max-w-2xl" style={{ fontSize: 'clamp(17px, 1.3vw, 21px)', lineHeight: 1.55 }}>
                            Every Southern Ontario neighbourhood scored against your budget, lifestyle, family
                            stage, commute and investment goals. The output is a ranked shortlist with the
                            reasoning behind each score — no upsells, no listings.
                        </p>
                        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-0 border-l border-t border-neutral-200 max-w-3xl">
                            {[
                                { k: 'Budget', v: 'What you can carry' },
                                { k: 'Lifestyle', v: 'How you live' },
                                { k: 'Family', v: 'Where you are' },
                                { k: 'Commute', v: 'Where you\u2019ll spend days' },
                            ].map((s) => (
                                <div key={s.k} className="border-r border-b border-neutral-200 p-6 bg-white">
                                    <div className="font-mono-data text-[11px] tracking-[0.22em] uppercase text-neutral-400">{s.k}</div>
                                    <div className="font-editorial text-[18px] mt-2 leading-tight">{s.v}</div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-12">
                            <button
                                onClick={startAssessment}
                                data-testid="match-intro-start"
                                className="inline-flex items-center gap-2 bg-brand-navy text-white px-8 py-4 text-[14px] font-medium hover:bg-neutral-800"
                                style={{ letterSpacing: '0.02em' }}
                            >
                                Begin the assessment
                                <ArrowRight className="w-4 h-4" />
                            </button>
                            <div className="mt-4 text-[11px] uppercase tracking-[0.22em] text-neutral-500 font-mono-data">
                                ~3 minutes · Free · Save to unlock the full report
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* ==================== ASSESSMENT ==================== */}
            {phase === 'assessment' && currentSection && (
                <section className="max-w-[1200px] mx-auto px-6 lg:px-10 pt-14 pb-24" data-testid="match-assessment">
                    {/* Progress rail */}
                    <div className="grid grid-cols-4 gap-0 border-l border-t border-neutral-200">
                        {sections.map((s, i) => (
                            <div key={s.id} className={`border-r border-b border-neutral-200 px-4 py-4 ${i === sectionIdx ? 'bg-brand-navy text-white' : (i < sectionIdx ? 'bg-white text-black' : 'bg-white text-neutral-400')}`}>
                                <div className="flex items-center justify-between">
                                    <div className={`font-mono-data text-[11px] tracking-[0.22em] uppercase ${i === sectionIdx ? 'text-white/70' : ''}`}>
                                        0{i + 1}
                                    </div>
                                    {i < sectionIdx && <Check className="w-4 h-4" />}
                                </div>
                                <div className="font-editorial text-[16px] mt-3 leading-tight">{s.title}</div>
                            </div>
                        ))}
                    </div>

                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentSection.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.45, ease: [0.22, 0.61, 0.36, 1] }}
                            className="mt-10"
                        >
                            <div className="text-[11px] tracking-[0.24em] uppercase text-neutral-500 font-mono-data">
                                {currentSection.eyebrow}
                            </div>
                            <h2 className="font-editorial mt-4 leading-[1.05]" style={{ fontSize: 'clamp(28px, 3.6vw, 44px)', letterSpacing: '-0.02em' }}>
                                {currentSection.kicker}
                            </h2>

                            <div className="mt-12 space-y-10">
                                {currentSection.questions.map((q) => (
                                    <QuestionField
                                        key={q.id}
                                        q={q}
                                        value={answers[q.id]}
                                        onChange={(v) => setAns(q.id, v)}
                                        onToggle={(v) => toggleMulti(q.id, v)}
                                    />
                                ))}
                            </div>

                            {error && (
                                <div className="mt-6 text-[13px] text-red-700 border border-red-300 bg-red-50 px-4 py-3" data-testid="match-error">
                                    {error}
                                </div>
                            )}

                            <div className="mt-12 pt-8 border-t border-neutral-200 flex items-center justify-between">
                                <button
                                    onClick={() => setSectionIdx((i) => Math.max(0, i - 1))}
                                    disabled={sectionIdx === 0}
                                    data-testid="match-back-btn"
                                    className="inline-flex items-center gap-2 border border-neutral-400 text-black px-6 py-3 text-[13.5px] disabled:opacity-40"
                                   
                                >
                                    <ArrowLeft className="w-4 h-4" /> Back
                                </button>
                                <button
                                    onClick={next}
                                    data-testid="match-next-btn"
                                    className="inline-flex items-center gap-2 bg-brand-navy text-white px-8 py-3.5 text-[14px] font-medium hover:bg-neutral-800"
                                    style={{ letterSpacing: '0.02em' }}
                                >
                                    {sectionIdx === sections.length - 1 ? 'See my matches' : 'Continue'}
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                            </div>
                        </motion.div>
                    </AnimatePresence>

                    {/* Email capture appears on last section for pre-signup lead capture */}
                    {sectionIdx === sections.length - 1 && (
                        <div className="mt-14 border border-neutral-200 bg-white p-6 lg:p-8" data-testid="match-email-capture">
                            <div className="overline">Optional — save your results</div>
                            <p className="mt-3 text-[14px] text-neutral-600 leading-relaxed max-w-xl">
                                Drop your email and we&apos;ll save this assessment so you can come back later, or
                                send you the report as a PDF after you unlock it.
                            </p>
                            <div className="mt-4 flex flex-col sm:flex-row gap-3 max-w-2xl">
                                <input
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    placeholder="First name"
                                    className="sm:w-40 border border-neutral-300 px-4 py-3 text-[14px] focus:outline-none focus:border-black"
                                   
                                    data-testid="match-email-first-name"
                                />
                                <input
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    type="email"
                                    placeholder="you@example.com"
                                    className="flex-1 border border-neutral-300 px-4 py-3 text-[14px] focus:outline-none focus:border-black"
                                   
                                    data-testid="match-email-input"
                                />
                            </div>
                        </div>
                    )}
                </section>
            )}

            {/* ==================== SUBMITTING ==================== */}
            {phase === 'submitting' && (
                <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-40 text-center" data-testid="match-submitting">
                    <div className="font-mono-data text-[11px] tracking-[0.24em] uppercase text-neutral-500">Scoring…</div>
                    <div className="font-editorial mt-6 italic text-neutral-700" style={{ fontSize: '28px' }}>
                        Running your answers against every neighbourhood.
                    </div>
                </div>
            )}

            {/* ==================== PREVIEW (locked) ==================== */}
            {phase === 'preview' && preview && (
                <MatchPreview preview={preview} onUnlockClick={() => setGateOpen(true)} />
            )}

            {/* ==================== FULL RESULTS ==================== */}
            {phase === 'results' && results && (
                <MatchResults results={results} />
            )}

            <MatchGate
                open={gateOpen}
                sessionId={preview?.session_id}
                onClose={() => setGateOpen(false)}
                onUnlocked={(res) => { setResults(res); setPhase('results'); setGateOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            />
        </div>
    );
}


// --------------------------------------------------------------------------
// Individual question field
// --------------------------------------------------------------------------
function QuestionField({ q, value, onChange, onToggle }) {
    return (
        <div data-testid={`match-q-${q.id}`}>
            <div className="font-editorial text-[20px] leading-tight tracking-editorial text-black">
                {q.label}
                {q.required && <span className="text-neutral-400 ml-2 text-[14px]">·  required</span>}
            </div>

            {q.type === 'number' && (
                <input
                    type="number"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={q.placeholder}
                    min={q.min}
                    max={q.max}
                    step={q.step}
                    className="mt-4 w-full max-w-md border-b border-neutral-300 focus:border-black px-0 py-3 text-[22px] font-editorial focus:outline-none bg-transparent tabular"
                    data-testid={`match-input-${q.id}`}
                />
            )}

            {q.type === 'single' && (
                <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-0 border-l border-t border-neutral-200">
                    {q.options.map((opt) => {
                        const sel = value === opt.value;
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                onClick={() => onChange(opt.value)}
                                data-testid={`match-opt-${q.id}-${opt.value}`}
                                className={`border-r border-b border-neutral-200 text-left px-5 py-4 text-[14px] leading-relaxed transition-colors ${sel ? 'bg-brand-navy text-white' : 'bg-white hover:bg-[#FAFAF8]'}`}
                               
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <span>{opt.label}</span>
                                    {sel && <Check className="w-4 h-4 mt-0.5 shrink-0" />}
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}

            {q.type === 'multi' && (
                <div className="mt-5 flex flex-wrap gap-2">
                    {q.options.map((opt) => {
                        const sel = value?.includes(opt.value);
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                onClick={() => onToggle(opt.value)}
                                data-testid={`match-opt-${q.id}-${opt.value}`}
                                className={`border px-4 py-2.5 text-[13px] transition-colors ${sel ? 'bg-brand-navy text-white border-black' : 'bg-white text-neutral-700 border-neutral-300 hover:border-brand-navy'}`}
                               
                            >
                                {opt.label}
                            </button>
                        );
                    })}
                </div>
            )}

            {q.type === 'scale' && (
                <div className="mt-5 max-w-lg">
                    <div className="flex items-center justify-between text-[11px] tracking-[0.22em] uppercase text-neutral-500 font-mono-data">
                        <span>{q.min_label}</span>
                        <span>{q.max_label}</span>
                    </div>
                    <div className="mt-3 flex gap-2">
                        {[1, 2, 3, 4, 5].map((n) => {
                            const sel = Number(value) === n;
                            return (
                                <button
                                    key={n}
                                    type="button"
                                    onClick={() => onChange(n)}
                                    data-testid={`match-scale-${q.id}-${n}`}
                                    className={`flex-1 h-12 border font-mono-data tabular text-[15px] transition-colors ${sel ? 'bg-brand-navy text-white border-black' : 'bg-white text-neutral-700 border-neutral-300 hover:border-brand-navy'}`}
                                   
                                >
                                    {n}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
